import { useRecipientFrameDraft } from './useRecipientFrameDraft';
import { trackFrameGift } from '@src/utils/frameGiftAnalytics';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import './PersonalGiftReceipt.css';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxGiftInfo } from '@lub0v/santa-utils';
import { Hint, Switch, TextFieldWithPresets, Typography, showDialog } from '@src/components';
import { AvatarFramePicker } from '@src/components/faces/AvatarFramePicker';
import { useAppSelector } from '@src/state/hooks';

export function PersonalGiftReceipt({
  boxKey,
  gift,
  onUpdated,
  busy = false,
  registerSubmit,
  registerFrameSave,
}: {
  registerFrameSave?: (save: () => Promise<void>) => void;
  busy?: boolean;
  registerSubmit?: (submit: () => Promise<void>) => void;
  boxKey: string;
  gift: BoxGiftInfo;
  onUpdated: (gift: BoxGiftInfo) => void;
}) {
  const { t } = useTranslation();
  const card = useAppSelector((s) => s.box.card);
  const { frame, enabled, select, save } = useRecipientFrameDraft(boxKey, gift.id, card?.frameName);
  useLayoutEffect(() => { registerFrameSave?.(save); });
  const [message, setMessage] = useState('');
  const [thanked, setThanked] = useState(!!gift.thankedAt);
  const [sentMessage, setSentMessage] = useState(gift.thankYouMessage ?? '');
  const [error, setError] = useState('');
  async function thank() {
    if (busy) return;
    const messageType = !message.trim() ? 'empty' : ['warm','magic','hug','holiday'].some(key => t('game.ward.personalThanks.' + key) === message.trim()) ? 'predefined' : 'custom';
    trackFrameGift('gift_frame_thank_clicked', { message_type: messageType });
    setError('');
    try {
      const r = await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/thank`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giftId: gift.id, message: message.trim(), thankAnonymously: false }),
      });
      if (!r.ok) throw Error();
      const updated = await r.json();
      if (updated.thankedRecorded) trackFrameGift('gift_frame_thank_sent', { message_type: messageType });
      onUpdated(updated);
      setSentMessage(updated.thankYouMessage ?? '');
      setThanked(true);
      showDialog({
        title: t('boxGift.thankSent'),
        testId: 'personal-gift-thanks-confirmation',
        submit: t('game.ward.personalGiftBack'),
        children: updated.thankYouMessage ? (
          <blockquote className="personal-gift-receipt__message">
            <MessageIcon aria-hidden="true" />
            <div className="personal-gift-receipt__message-copy">
              <Typography as="p" role="meta/helper">{t('game.ward.personalGiftYourThanks')}</Typography>
              <span>{updated.thankYouMessage}</span>
            </div>
          </blockquote>
        ) : undefined,
      });
    } catch {
      setError(t('boxGift.error'));
      throw new Error('Thank-you was not sent');
    }
  }
  useLayoutEffect(() => { registerSubmit?.(thank); });
  return (
    <div className="giftee-frame-preview" data-testid="personal-gift-receipt">
      <Typography as="p" role="body/base">
        {t('game.ward.personalGiftFromSanta')}
      </Typography>
      {gift.message && <blockquote className="personal-gift-receipt__message"><MessageIcon aria-hidden="true" /><div className="personal-gift-receipt__message-copy"><Typography as="p" role="meta/helper">{t('game.ward.personalGiftSantaMessage')}</Typography><span>{gift.message}</span></div></blockquote>}
      <fieldset className="personal-gift-receipt__frames">
      <AvatarFramePicker
        card={card}
        selectedFrame={frame}
        onChange={(value) => {
          select(value, true);
        }}
        testIdPrefix="personal-gift-frame"
      />
      </fieldset>
      <div className="personal-gift-receipt__toggle-container">
      <Switch checked={enabled} onCheckedChange={value => select(frame, value)}
        label={t('game.ward.personalGiftUseFrame')} testId="personal-gift-frame-toggle" />
      </div>
      {thanked ? (
        <>
          {!sentMessage && <Typography as="p" role="body/base">{t('boxGift.thankSent')}</Typography>}
          {sentMessage && <blockquote className="personal-gift-receipt__message" data-testid="personal-gift-sent-message"><MessageIcon aria-hidden="true" /><div className="personal-gift-receipt__message-copy"><Typography as="p" role="meta/helper">{t('game.ward.personalGiftYourThanks')}</Typography><span>{sentMessage}</span></div></blockquote>}
        </>
      ) : (
        <>
          <TextFieldWithPresets
            presets={['warm', 'magic', 'hug', 'holiday'].map((key) =>
              t('game.ward.personalThanks.' + key),
            )}
            presetsLabel={t('game.ward.frameMessagePresets')}
            heading={t('boxGift.thankTitle')}
            label={t('game.ward.frameMessageLabel')}
            value={message}
            onValueChange={setMessage}
            maxLength={140}
            rows={2}
            showCount
            testId="personal-gift-thanks"
          />

        </>
      )}
      {error && <Hint tone="error">{error}</Hint>}
    </div>
  );
}
